/**
 * Read the message an edge function actually sent on a non-2xx response.
 *
 * `supabase.functions.invoke` reports every non-2xx as a FunctionsHttpError whose
 * `.message` is the FIXED SDK string "Edge Function returned a non-2xx status
 * code", and `data` is null — so the function's `{ error: "…" }` body is reachable
 * only through `.context`, a Response-like. Showing `.message` tells the user
 * nothing ("non-2xx") where the function told them exactly what was wrong.
 *
 * `clone()` first: the body is a stream and reading it twice throws. Returns
 * `fallback` whenever no readable string message exists.
 *
 * The same parsing lives privately in useContractorRegistrationUpload and
 * useOnboardingNdaSession; this is the shared form for new callers.
 */
interface FunctionsHttpErrorLike {
  message?: string;
  name?: string;
  context?: { status?: number; clone(): { json(): Promise<unknown> } };
}

export async function readEdgeFunctionError(error: unknown, fallback: string): Promise<string> {
  const typed = error as FunctionsHttpErrorLike | null;
  if (!typed?.context) return fallback;
  let body: unknown;
  try {
    body = await typed.context.clone().json();
  } catch {
    return fallback;
  }
  if (typeof body !== "object" || body === null) return fallback;
  const message = (body as { error?: unknown }).error;
  return typeof message === "string" && message.trim() !== "" ? message : fallback;
}

// ── Plain-language descriptions ─────────────────────────────────────────────
//
// Edge functions answer a refusal with a machine code (`{ error: "image_too_large" }`).
// The codes below are the ones shared across many functions, worded for the person
// at the screen: what went wrong and what to do about it. A code not listed here is
// still shown, humanised, rather than replaced by a generic sentence — a specific
// but terse message beats an accurate-sounding vague one.
const CODE_MESSAGES: Record<string, string> = {
  unauthorized: "Your session has expired. Sign out, sign back in, and try again.",
  insufficient_permission:
    "You don't have permission to do this. Ask an admin to check your module access.",
  permission_check_failed:
    "Your permissions couldn't be checked. Try again; if it keeps happening, tell IT.",
  forbidden: "You don't have permission to do this. Ask an admin to check your module access.",
  bad_request: "The request was incomplete. Refresh the page and try again.",
  invalid_request: "The request was incomplete. Refresh the page and try again.",
  internal_error: "Something went wrong on the server. Try again; if it keeps happening, tell IT.",
  storage_write_failed: "The file couldn't be saved. Try again in a moment.",
  image_too_large: "The image is too large.",
  file_too_large: "The file is too large.",
  payload_too_large: "The file is too large.",
  png_required: "The image must be a PNG file.",
  empty_image: "The image file is empty.",
  pin_required: "Enter your e-signature PIN.",
  invalid_pin: "Your e-signature PIN wasn't accepted. Check it and try again.",
  no_signature_on_file:
    "You have no signature on file. Upload one in Settings before approving documents.",
  no_profile_row: "No staff profile is linked to your account. Ask an admin to set one up.",
  rate_limited: "Too many attempts in a short time. Wait a minute and try again.",
};

/** Used when the body carries no code at all, keyed on the HTTP status. */
function statusMessage(status: number | undefined): string {
  if (status === 400) return "The request was rejected as invalid. Refresh the page and try again.";
  if (status === 401) return CODE_MESSAGES.unauthorized;
  if (status === 403) return CODE_MESSAGES.insufficient_permission;
  if (status === 404) return "The record or service couldn't be found. Refresh the page and try again.";
  if (status === 409) return "This conflicts with a change made elsewhere. Refresh the page and try again.";
  if (status === 413) return "The file is too large.";
  if (status === 429) return CODE_MESSAGES.rate_limited;
  if (status !== undefined && status >= 500) return CODE_MESSAGES.internal_error;
  return "The request failed.";
}

// A code is one case throughout: `image_too_large`, `UNAUTHORIZED`, `NO_RECIPIENT`.
// Mixed-case text ("Quote not found.") is a sentence and is shown as written.
const MACHINE_CODE = /^[a-z][a-z0-9]*(_[a-z0-9]+)*$|^[A-Z][A-Z0-9]*(_[A-Z0-9]+)*$/;

/** A server-written explanation: mixed-case prose, short enough for a toast. */
function isUserFacingMessage(value: string): boolean {
  return value.length <= 300 && !MACHINE_CODE.test(value) && /\s/.test(value);
}

function humaniseCode(code: string): string {
  const words = code.toLowerCase().replace(/_/g, " ");
  return `${words.charAt(0).toUpperCase()}${words.slice(1)}.`;
}

function formatBytes(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${+(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.round(bytes / 1024)} KB`;
}

/**
 * Turn any `functions.invoke` error into one specific, readable sentence, followed
 * by a short reference (function name and status) so a screenshot in a support
 * email identifies the failure without a log search.
 *
 *   HTTP error   → the body's `error` / `message`. A code (either case) paired with a
 *                  sentence in `message` shows the sentence — the function's own
 *                  explanation — except on a 5xx or INTERNAL_ERROR, where `message`
 *                  can be a raw database diagnostic. Otherwise a known code is mapped,
 *                  an unknown one humanised (with a short `detail`), a sentence shown
 *                  as-is; status fallback.
 *   Fetch error  → could not reach the server (network).
 *   Relay error  → the platform failed to reach the function.
 */
export async function describeEdgeFunctionError(
  error: unknown,
  functionName: string,
): Promise<string> {
  const typed = error as FunctionsHttpErrorLike | null;
  const name = typed?.name ?? "";

  if (name === "FunctionsFetchError") {
    return `Couldn't reach the server. Check your internet connection and try again. [${functionName}]`;
  }
  if (name === "FunctionsRelayError") {
    return `The server didn't respond properly. Try again in a moment. [${functionName}]`;
  }

  const status = typed?.context?.status;
  const ref = `[${functionName}${status ? ` · ${status}` : ""}]`;

  let body: Record<string, unknown> | null = null;
  if (typed?.context) {
    try {
      const parsed = await typed.context.clone().json();
      if (typeof parsed === "object" && parsed !== null) body = parsed as Record<string, unknown>;
    } catch {
      // Not JSON (an HTML gateway page, an empty body) — fall through to the status.
    }
  }

  const errorField = typeof body?.error === "string" ? body.error.trim() : "";
  const messageField = typeof body?.message === "string" ? body.message.trim() : "";
  const raw = errorField || messageField;
  const detail = typeof body?.detail === "string" ? body.detail.trim() : "";
  const isCode = raw !== "" && MACHINE_CODE.test(raw);
  const codeKey = isCode ? raw.toLowerCase() : raw;
  const serverFault = (status !== undefined && status >= 500) || codeKey === "internal_error";

  let text: string;
  if (!raw) {
    text = statusMessage(status);
  } else if (isCode && errorField && !serverFault && isUserFacingMessage(messageField)) {
    // `{ error: "NO_RECIPIENT", message: "No active contact with an email…" }` —
    // the sentence is the specific explanation; the code alone would lose it.
    text = messageField;
  } else if (CODE_MESSAGES[codeKey]) {
    text = CODE_MESSAGES[codeKey];
  } else if (isCode) {
    text = humaniseCode(raw);
    // An unknown code is terse; a short server-supplied detail is the best context
    // available. Length-capped so a stack-like string never fills a toast.
    if (detail && detail.length <= 200) text = `${text} ${detail}`;
  } else {
    text = raw;
  }

  // A size refusal that states its limit should pass the limit on.
  const maxBytes = typeof body?.maxBytes === "number" ? body.maxBytes : null;
  if (maxBytes && /too large/i.test(text)) {
    text = `${text.replace(/\.$/, "")} — the maximum is ${formatBytes(maxBytes)}.`;
  }

  return `${text} ${ref}`;
}
